/****************************************************************************
*			       Copyright © 2023 HeartcoR Solutions
*                        All rights reserved.
*		Updated under contract by CriTech Research, Inc.
*
* File Name:    ResetPassword.js
* Project: 		HeartcoR EDAAS End-To-End Anomaly Corrections
* File Type:	Javascript File
* Description:  This file contains routines to reset the user password.
*
* Revision History:
* Date       * Engineer * ECP   * Changes
* Unknown    * N/A      * N/A   * File created.
* 2023-06-07 * CED      * 0001  * Implemented changes to fix Anomaly #209.
*                                 Password not encrypted during transmission
* 2023-06-20 * CED      * 0001  * Updated per CRI-81532-SCRR4001-0100-RR0001-0100
*****************************************************************************/
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from "react-router-dom";
import { Alert, Button, Form } from 'reactstrap';
import ErrorBoundary from './ErrorBoundary';
import * as Accounts from '../store/Accounts';
import * as elements from '../data/Elements';
import * as paths from '../data/Paths';
import * as utility from '../data/Utility';
import { getRequest } from '../data/Fetch';
import * as HeartcoRCommon from '../data/HeartcoR_Common'; //import HeartcoR_Common module from the common package

//***********************************************************************
//
//  Name: ResetPasswordInternal
//
//  Description: React component implementing routines to perform a password reset
//
//  Inheritance: React.Component
//
//  Friendship: public
//
//***********************************************************************
class ResetPasswordInternal extends React.Component {
    constructor(props) {
        super(props);

        const code = utility.propCheck(props.code, "");
        const confirmed = utility.propCheck(props.confirmed, false);
        const id = utility.propCheck(props.id, "");
        const oldRequired = utility.propCheck(props.oldRequired, false);

        var message = "";

        if (confirmed) {
            message = "Account successfully confirmed - please reset password following the requirements below.";
        }

        this.state = {
            code: code,
            id: id,
            error: false,
            message: message,
            newPassword: "",
            newPasswordConfirm: "",
            oldPassword: "",
            oldRequired: oldRequired
        };

        this.handleClick = this.handleClick.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleNewPassword = this.handleNewPassword.bind(this);
        this.handleNewPasswordConfirm = this.handleNewPasswordConfirm.bind(this);
        this.handleOldPassword = this.handleOldPassword.bind(this);
        this.setError = this.setError.bind(this);
    }

    checkPasswordValid(password) {
        if (password === "") {
            return null;
        }

        var character = '';
        var digit = false;
        var i = 0;
        var lowercase = false;
        var special = false;
        var uppercase = false;

        for (i = 0; i < password.length; i++) {
            character = password.charAt(i);
            if (!isNaN(character * 1)) {
                digit = true;
            }
            else {
                if (/[~`!#$%^&*+=\-[\]\\';,/{}|\\":<>?]/g.test(character)) {
                    special = true;
                }
                else if (character === character.toUpperCase()) {
                    uppercase = true;
                }
                else if (character === character.toLowerCase()) {
                    lowercase = true;
                }
            }
        }

        if (password.length >= 8 &&
            lowercase &&
            uppercase &&
            digit &&
            special) {
            return true;
        }
        return false;
    }

    /****************************************************************************
    *  Name: handleClick
    *
    *  Description: Handle action when the button to change the password is pressed
    *
    *  Interface:
    *    Parameters:
    *      Name:               In / Out / In-Out          Range
    *      None                N/A                        N/A
    *
    *    Globals:
    *      Name:               In / Out / In-Out          Range
    *      this                In-Out                     Public
    *
    *  Returns: None
    *
    *  Notes: Function updated per bug # 209 Password not encrypted during transmission
    *
    *****************************************************************************/
    handleClick() {
        var message = "Missing or invalid fields.";
        const newPassword = this.state.newPassword;
        const newPasswordValid = this.checkPasswordValid(newPassword);
        const oldRequired = this.state.oldRequired;
        const oldPassword = this.state.oldPassword;
        var oldValid = true;



        if (oldRequired &&
            oldPassword === "") {
            oldValid = false;
        }

        if (oldValid &&
            newPasswordValid != null &&
            newPasswordValid &&
            newPassword === this.state.newPasswordConfirm) {
            var code = this.state.code;
            var status = null;
            (async () => {
                const AESKey = await getRequest("settings/GetAESKey", null); //Gets the AES Encryption Key
                const AESIV = await getRequest("settings/GetAESIV", null); //Gets the AES Initialization Vector
                HeartcoRCommon.setPasswordKeys(AESKey, AESIV); //Sets the Key and Initialization Vector of the HeartcoR_Common module 
                if (oldRequired) {
                    const oldEncryptedPassword = HeartcoRCommon.EncryptPassword(oldPassword) //Encrypt the old password if required
                    code = await this.props.accountsDispatch.getPasswordResetToken(oldEncryptedPassword);
                }
                if (code != null &&
                    code !== "") {
                    const newEncryptedPassword = HeartcoRCommon.EncryptPassword(newPassword) //Encrypt the new password
                    status = await this.props.accountsDispatch.resetPassword(this.state.id, code, newEncryptedPassword);
                }
            })().then(() => {
                if (status != null) {
                    if (status) {
                        this.props.history.push(paths.homeResetSuccess);
                    }
                    else {
                        this.setError("Password reset token expired - please use the \"Forgot Password\" feature on the sign in page to try again, or contact HCS support for assistance.");
                    }
                }
                else {
                    this.setError(message);
                }
            });
        }
        else {
            this.setError(message);
        }
    }

    handleKeyDown(e) {
        if (e.key === 'Enter') {
            this.handleClick();
        }
    }

    handleNewPassword(e) {
        this.setState({
            error: false,
            message: "",
            newPassword: e.target.value
        });
    }

    handleNewPasswordConfirm(e) {
        this.setState({
            error: false,
            message: "",
            newPasswordConfirm: e.target.value
        });
    }

    handleOldPassword(e) {
        this.setState({
            error: false,
            message: "",
            oldPassword: e.target.value
        });
    }

    render() {
        const alertColor = this.state.error ? "danger" : "primary";
        const message = this.state.message;
        const displayMessage = message !== "";
        const newPassword = this.state.newPassword;
        const newPasswordValid = this.checkPasswordValid(newPassword);
        const newPasswordGroup = elements.getFormGroup("New Password", false, elements.getInput("newPasswordInput", false, this.handleNewPassword, this.handleKeyDown, elements.inputTypes.password, newPassword, newPasswordValid));
        const newPasswordConfirm = this.state.newPasswordConfirm;
        var newPasswordConfirmValid = null;
        if (newPasswordConfirm !== "") {
            newPasswordConfirmValid = newPassword === newPasswordConfirm;
        }
        const newPasswordConfirmGroup = elements.getFormGroup("Confirm New Password", false, elements.getInput("newPasswordConfirmInput", false, this.handleNewPasswordConfirm, this.handleKeyDown, elements.inputTypes.password, newPasswordConfirm, newPasswordConfirmValid));
        const oldPasswordGroup = elements.getFormGroup("Old Password", false, elements.getInput("oldPasswordInput", false, this.handleOldPassword, this.handleKeyDown, elements.inputTypes.password, this.state.oldPassword));
        const submit = "Submit";

        return (
            <Form>
                <h1>Reset Password</h1>
                {displayMessage && <Alert color={alertColor}>{message}</Alert>}
                <p className="gap white-space">Password requirements:<br />    - Length must be at least 8 characters<br />    - At least one character must be lowercase<br />    - At least one character must be uppercase<br />    - At least one character must be a digit (0-9)<br />    - At least one character must be non-alphanumeric</p>
                <div className="gap" />
                {this.state.oldRequired && oldPasswordGroup}
                {newPasswordGroup}
                {newPasswordConfirmGroup}
                <div className="button-gap">
                    <Button className="button" title={submit} onClick={this.handleClick}>{submit}</Button>
                </div>
            </Form>
        );
    }

    setError(message) {
        this.setState({
            error: true,
            message: message
        });
    }
}

const ResetPasswordConnect = withRouter(connect(
    null,
    dispatch => ({
        accountsDispatch: bindActionCreators(Accounts.actionCreators, dispatch)
    })
)(ResetPasswordInternal));

export default class ResetPassword extends React.Component {
    render() {
        var code = "";
        var confirmed = false;
        var id = "";
        var oldRequired = false;

        const propsSearch = this.props.location.search;
        if (propsSearch != null) {
            var codeProp = null;
            var confirmedProp = null;
            var idProp = null;

            var split = propsSearch.split("&");
            if (split.length >= 1) {
                idProp = split[0];
            }
            if (split.length >= 2) {
                codeProp = split[1];
            }
            if (split.length >= 3) {
                confirmedProp = split[2];
            }

            if (idProp != null) {
                var idSearch = "?id=";
                var idIndex = idProp.indexOf(idSearch);
                if (idIndex >= 0) {
                    id = idProp.substr(idIndex + idSearch.length);
                }
            }
            if (codeProp != null) {
                var codeSearch = "code=";
                var codeIndex = codeProp.indexOf(codeSearch);
                if (codeIndex >= 0) {
                    code = codeProp.substr(codeIndex + codeSearch.length);
                }
            }
            if (confirmedProp != null) {
                var confirmedSearch = "confirmed";
                var confirmedIndex = confirmedProp.indexOf(confirmedSearch);
                if (confirmedIndex >= 0) {
                    confirmed = true;
                }
            }
        }

        const propsState = this.props.location.state;
        if (propsState != null) {
            if (propsState.id != null) {
                id = propsState.id;
            }
            if (propsState.oldRequired != null) {
                oldRequired = propsState.oldRequired;
            }
        }

        return (
            <ErrorBoundary>
                <ResetPasswordConnect code={code} confirmed={confirmed} id={id} oldRequired={oldRequired} />
            </ErrorBoundary>
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/ResetPassword.js